import React from 'react'
import { createRoot } from 'react-dom/client'

import { ReduxRouter as Router } from '@lagunovsky/redux-react-router'
import { Provider } from 'react-redux'

import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import {
  initializeFaro,
  ReactIntegration,
  getWebInstrumentations,
  createReactRouterV6Options,
  FaroErrorBoundary,
  faro,
} from '@grafana/faro-react'

import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import configureStore, { history } from './configureStore'
import { fetchAuth } from './actions/auth'
import App from './App'

import '@uswds/uswds'
import './index.scss'

const shouldStartMirage =
  !window.location.href.match(/https:\/\/.*\.tanfdata\.acf\.hhs\.gov/) &&
  (process.env.REACT_APP_USE_MIRAGE === 'true' ||
    process.env.REACT_APP_PA11Y_TEST === 'true')

// Initialize FaroSDK
if (process.env.REACT_APP_ENABLE_RUM === 'true') {
  initializeFaro({
    url: process.env.REACT_APP_FARO_ENDPOINT,
    app: {
      name: 'tdp-frontend-rum',
      version: process.env.REACT_APP_VERSION,
      environment: process.env.NODE_ENV,
    },
    // Set a reasonable sample rate to control data volume
    sessionSampleRate: 0.5,
    instrumentations: [
      // Load the default Web instrumentations
      ...getWebInstrumentations({ captureConsole: true }),
      // Trace API calls and other async operations
      new TracingInstrumentation(),
      // Add React-specific instrumentation
      new ReactIntegration({
        // Track components that take >50ms to render
        componentRenderThreshold: 50,
        router: createReactRouterV6Options({
          createRoutesFromChildren,
          matchRoutes,
          Routes,
          useLocation,
          useNavigationType,
        }),
      }),
    ],
    meta: {
      // Add any application-specific metadata here
      appType: 'TANF Data Portal',
      service_name: 'tdp-frontend',
    },
  })
}

/* istanbul ignore next */
const ErrorProvider = ({ children }) => {
  return !faro || !faro.api ? (
    <>{children}</>
  ) : (
    <FaroErrorBoundary>{children}</FaroErrorBoundary>
  )
}

const startApp = async () => {
  if (shouldStartMirage) {
    // Mirage must start before auth_check so local stubbed auth routes exist.
    const { default: startMirage } = await import('./mirage')
    startMirage()
  }

  // call auth_check
  const store = configureStore()
  store.dispatch(fetchAuth())

  const container = document.getElementById('root')
  const root = createRoot(container)
  root.render(
    <Provider store={store}>
      <Router store={store} history={history}>
        <ErrorProvider>
          <App />
        </ErrorProvider>
      </Router>
    </Provider>
  )

  // expose store when run in Cypress
  if (window.Cypress) {
    window.store = store
  }
}

startApp()
